import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import ProtectedRoute from './ProtectedRoute';
import userStore from '../stores/UserStore';
import { useAppTheme } from '../hooks/useAppTheme';
import type { ThemedSxStyle } from '../types/themedStyles';
import { $loadingContainer } from '../theme/styles';
import {
  isPlatformOrgAdminUserRole,
  isSuperAdminUserRole,
  userHasPlatformOrgAccess,
} from '../utilities/platformAdminAccess';
import { isFacilitatorUserRole } from '../utilities/orgAccess';

interface SuperAdminOnlyRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  /** When true, platform org admins (`admin`) may access (org tab tools). Super admins always allowed. */
  allowOrgAdmin?: boolean;
  /** When true, facilitators may access. Super admins always allowed. */
  allowFacilitator?: boolean;
}

const SuperAdminOnlyRoute: React.FC<SuperAdminOnlyRouteProps> = observer(({ 
  children, 
  redirectTo = '/loginscreen',
  allowOrgAdmin = false,
  allowFacilitator = false,
}) => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const { themed, theme: appTheme } = useAppTheme();

  useEffect(() => {
    const userRole = userStore.user?.userRole;
    const isDemoUser = userStore.user?.userId === 'demo-user';
    const allowed =
      isDemoUser ||
      isSuperAdminUserRole(userRole) ||
      isSuperAdminUserRole(userStore.user?.platformAdminRole) ||
      (allowOrgAdmin && userHasPlatformOrgAccess(userStore.user)) ||
      (allowFacilitator && isFacilitatorUserRole(userRole));

    setHasAccess(allowed);
    setIsCheckingAuth(false);
  }, [userStore.user?.userId, userStore.user?.userRole, userStore.user?.platformAdminRole, allowOrgAdmin, allowFacilitator]);

  // Show loading while checking
  if (isCheckingAuth) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        sx={themed([$loadingContainer, $loadingContainerBg])}
      >
        <CircularProgress sx={themed($progress)} />
        <Typography variant="body1" sx={themed($loadingText)}>
          Checking access permissions...
        </Typography>
      </Box>
    );
  }

  // If not authenticated, redirect to login (so user is asked to log in, not shown "Access Denied")
  if (!userStore.user) {
    return (
      <ProtectedRoute redirectTo={redirectTo}>
        {children}
      </ProtectedRoute>
    );
  }

  // If authenticated but no access (not super admin), show error message
  if (hasAccess === false) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        sx={themed($errorContainer)}
      >
        <Alert 
          severity="error" 
          sx={themed($errorAlert)}
        >
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            {allowOrgAdmin && isPlatformOrgAdminUserRole(userStore.user?.userRole)
              ? 'You do not have permission to access this area. This page is only available to super administrators.'
              : allowOrgAdmin
                ? 'You do not have permission to access this area. Only super administrators and org admins can access this page.'
                : 'You do not have permission to access this area. Only super administrators can access this page.'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // If has access, render the protected route (which will handle authentication)
  return (
    <ProtectedRoute redirectTo={redirectTo}>
      {children}
    </ProtectedRoute>
  );
});

const $loadingContainerBg: ThemedSxStyle = ({ colors }) => ({
  backgroundColor: colors.background,
  transition: 'background-color 0.3s ease'
});

const $progress: ThemedSxStyle = ({ colors }) => ({
  color: colors.palette.main,
  mb: 2
});

const $loadingText: ThemedSxStyle = ({ colors }) => ({
  color: colors.text
});

const $errorContainer: ThemedSxStyle = ({ colors }) => ({
  backgroundColor: colors.background,
  transition: 'background-color 0.3s ease',
  p: 3
});

const $errorAlert: ThemedSxStyle = () => ({
  maxWidth: 600,
  '& .MuiAlert-message': {
    width: '100%'
  }
});

export default SuperAdminOnlyRoute;

